import React, { useState, useEffect, useRef } from 'react';
import {
  COOKIE_NAMES,
  GeolocationType,
  getUserProfile,
  secureStorage
} from '../../helpers/global-storage';
import { personalizeAddressData } from '../../helpers/personalize';
import { ExtendedWindowType } from '../../helpers/global-types';

type AutocompleteProp = {
  label: string;
  name: string;
  placeholder: string;
  tooltip?: string;
  type: string;
  value: string;
  setValue: React.Dispatch<
    React.SetStateAction<{ address: string; zip: string; country: string }>
  >;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  errorLabel: string;
  clearLabel: string;
  countries: string[];
};

const Autocomplete: React.FC<AutocompleteProp> = ({
  label,
  name,
  placeholder,
  tooltip,
  type,
  value,
  setValue,
  errorLabel,
  clearLabel,
  error,
  setError,
  countries
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const autocompleteInput = useRef(null);
  const [touched, setTouched] = useState(false);
  const [showClearButton, setShowClearButton] = useState(false);
  const options = {
    componentRestrictions: { country: countries },
    fields: ['formatted_address', 'address_components', 'geometry'],
    types: ['postal_code', 'route']
  };

  useEffect(() => {
    if (autocompleteInput.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        autocompleteInput.current as HTMLInputElement,
        options
      );
    }

    if (autocompleteRef.current) {
      autocompleteRef.current.addListener('place_changed', async function () {
        const place = await (
          autocompleteRef.current as google.maps.places.Autocomplete
        ).getPlace();

        const _place = place.address_components as {
          types: string[];
          long_name: string;
          short_name: string;
          geometry: object;
        }[];

        const _location = place.geometry?.location as {
          lat: () => number;
          lng: () => number;
        };

        const zipCode = _place.find(item => item.types.includes('postal_code'));
        if (!zipCode) {
          setError(true);
          return;
        }
        const country = _place.find(item => item.types.includes('country'));
        const streetNumber = _place.find(item =>
          item.types.includes('street_number')
        );
        const street = _place.find(item => item.types.includes('route'));
        const state = _place.find(item =>
          item.types.includes('administrative_area_level_1')
        );
        const city = _place.find(item => item.types.includes('locality'));
        const latitude = _location.lat();
        const longitude = _location.lng();

        let queryCountry = country?.short_name;
        let queryZip = zipCode?.long_name?.trim();

        if (queryCountry?.toUpperCase() === 'US') {
          queryCountry = 'USA';
        }

        if (queryCountry?.toUpperCase() === 'CA') {
          queryCountry = 'CAN';

          //remove white space from postal code
          queryZip = queryZip?.replace(/\s/g, '');
        }

        setValue(prev => ({
          ...prev,
          [name]: place.formatted_address ? place.formatted_address : '',
          zip: queryZip ? queryZip : '',
          country: queryCountry ? queryCountry : ''
        }));
        if (zipCode) {
          setError(false);
        }

        const geolocationData: GeolocationType = {
          latitude,
          longitude,
          zip: zipCode?.long_name as string,
          country: country?.short_name as string,
          address: place.formatted_address
        };

        secureStorage.set(
          COOKIE_NAMES.geolocation,
          JSON.stringify(geolocationData)
        );

        (window as ExtendedWindowType).dataLayer.push({
          event_attributes: {
            zip_code: geolocationData.zip,
            zip_code_country: geolocationData.country
          },
          event: 'zip_code',
          event_category: 'geo info',
          event_action: 'zip code updated'
        });

        const fullStreet = `${streetNumber?.long_name} ${street?.long_name}`;

        const sitecorePersonalizeAddress = {
          Street: fullStreet.trim(),
          City: city?.long_name,
          State: state?.short_name,
          Country: country?.short_name,
          PostalCode: zipCode?.long_name,
          Email: getUserProfile()?.email,
          PartitionKey: new Date().getFullYear().toString(),
          RowKey: new Date().getTime().toString()
        };

        const { Street, City, State, Country, PostalCode } =
          sitecorePersonalizeAddress;

        if (streetNumber && Street && City && State && Country && PostalCode) {
          personalizeAddressData(sitecorePersonalizeAddress);
        }

        (window as ExtendedWindowType).dataLayer.push({
          event_attributes: {},
          event: 'typeahead',
          event_category: 'typeahead',
          event_action: 'click',
          event_label: 'address'
        });
      });
    }
  }, []);

  useEffect(() => {
    if (
      autocompleteInput.current &&
      value &&
      value !== (autocompleteInput.current as HTMLInputElement)?.value
    ) {
      (autocompleteInput.current as HTMLInputElement).value = value;
    }
  }, [value, autocompleteInput.current]);

  const handleClear = () => {
    if (autocompleteInput.current) {
      (autocompleteInput.current as HTMLInputElement).value = '';
    }
    setValue(prev => ({
      ...prev,
      [name]: '',
      zip: '',
      country: ''
    }));

    setShowClearButton(false);
  };

  const handleChange = () => {
    if (autocompleteInput.current) {
      const input = (autocompleteInput.current as HTMLInputElement).value;
      if (input.length > 0) {
        setShowClearButton(true);
      } else {
        setShowClearButton(false);
      }
    }
  };

  const handleTypeAheadViewed = () => {
    const global = (window as ExtendedWindowType).dataLayer;
    const viewed = {
      event_attributes: {},
      event: 'typeahead',
      event_category: 'typeahead',
      event_action: 'viewed',
      event_label: 'address'
    };
    if (global.length > 0) {
      if (global[global.length - 1].event_action !== 'viewed') {
        global.push(viewed);
      }
    } else {
      global.push(viewed);
    }
  };

  return (
    <div
      className={`field field--autocomplete${tooltip ? ' field--tooltip' : ''}${
        error ? ' field--error' : ''
      }
      `}
    >
      <div className="field">
        <label
          className="field__label"
          htmlFor={label.trim().replace(' ', '_').toLowerCase()}
        >
          {label}
          {tooltip ? (
            <button className="field__tooltip" title="tooltip">
              <div className="field__tooltip-box">{tooltip}</div>
            </button>
          ) : null}
        </label>
        <input
          className="field__input"
          type={type}
          ref={autocompleteInput}
          id={label.trim().replace(' ', '_').toLowerCase()}
          name={name}
          placeholder={placeholder}
          onInput={() => {
            touched && setError(true);
          }}
          onChange={() => {
            touched && setError(true);
            handleChange();
            handleTypeAheadViewed();
          }}
          onBlur={() => setTouched(true)}
        />

        {(value?.length > 0 || showClearButton) && (
          <button
            className="field__input-close header__input-close"
            title={clearLabel || 'clear'}
            aria-label={clearLabel || 'clear'}
            onClick={handleClear}
          ></button>
        )}
      </div>
      {error && <span className="field__error-msg">{errorLabel}</span>}
    </div>
  );
};

export default Autocomplete;
